import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useAuthContext } from 'contexts/auth.context'
import { useOutletContext } from 'react-router'
import { SocioeducationMeasureIdentificationType } from 'types/socioeducation-measure-identification.type'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import { MutableRefObject, useEffect } from 'react'
import {
  SpanSubtitleCustom,
  SpanTitleCustom,
} from 'pages/SocioeducationMeasureEdit/FamilyData/style'
import { FamiliarCompositionList } from 'pages/SocioeducationMeasureEdit/FamilyData/components/FamiliarCompositionList'
import {
  SocioeducationMeasureFamilyDataDefaultValues,
  SocioeducationMeasureFamilyDataSchema,
  SocioeducationMeasureFamilyDataType,
} from 'types/socioeducation-measure-family-data.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { familiarReferenceV2Init } from 'types/familiar-reference-v2.type'
import FormInput from 'components/FormInput'
import { useMeioAbertoDadosFamiliares } from 'hooks/meio-aberto-dados-familiares.hook'
import { useFamiliarReferenceOptions } from 'pages/TeenagersEdit/components/TabFamiliarReference/components/DialogForm/hooks/select-option.hook'

type OutletContextType = {
  currentStep: number
  pessoaFisicaId?: number
  formRefs: MutableRefObject<Record<number, { submit: () => Promise<void> | void }>>
}

export const FamilyDataPage = () => {
  const { id } = useParams()
  const { handleHasPermission } = useAuthContext()
  const { dadosFamiliares, handleSalvar: handleSalvarDadosFamiliares } =
    useMeioAbertoDadosFamiliares(id)
  const familiarReferenceOptions = useFamiliarReferenceOptions()

  const { currentStep, formRefs } = useOutletContext<OutletContextType>()

  const methods = useForm<SocioeducationMeasureFamilyDataType>({
    resolver: zodResolver(SocioeducationMeasureFamilyDataSchema),
    defaultValues: SocioeducationMeasureFamilyDataDefaultValues,
  })

  const {
    watch,
    control,
    setValue,
    clearErrors,
    setError,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
  } = methods
  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const doNotHasEditPermission = !handleHasPermission(Funcionalidades.meioAberto.editar)

  useEffect(() => {
    if (dadosFamiliares) {
      reset(dadosFamiliares)
    }
  }, [dadosFamiliares])

  const handleSalvar = async (data?: SocioeducationMeasureFamilyDataType) => {
    await handleSalvarDadosFamiliares(data)
    return Promise.resolve()
  }

  const onError = (errors: FieldErrors<SocioeducationMeasureFamilyDataType>) => {
    clearErrors()
    const recursiveSetError = (
      errors: FieldErrors<SocioeducationMeasureFamilyDataType>,
      parentPath = '',
    ) => {
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }
    recursiveSetError(errors)
    console.log('onError', errors)
    return Promise.reject(errors)
  }

  formRefs.current[currentStep] = {
    submit: async () => {
      if (isDirty && hasDirtyFields && handleHasPermission(Funcionalidades.meioAberto.editar)) {
        await handleSubmit(handleSalvar, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSalvar, onError)}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'body1'} color={'secondary'}>
              <strong>COMPOSIÇÃO FAMILIAR</strong>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FamiliarCompositionList
              watch={watch}
              control={control}
              fieldArrayName={'referenciasFamiliares'}
              initialValuesFieldName={'referenciasFamiliares'}
              setValue={setValue}
              initialValues={[familiarReferenceV2Init]}
              buttonLabel={'COMPOSIÇÃO FAMILIAR'}
              disabled={doNotHasEditPermission}
              familiarReferenceOptions={familiarReferenceOptions}
              labels={{
                labelDeleteItem: 'Tem certeza que deseja excluir a composição familiar?',
              }}
            />
          </Grid>
          <Grid item xs={12} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <SpanTitleCustom>
              RELAÇÕES FAMILIARES E COMUNITÁRIAS
              <SpanSubtitleCustom>
                {' '}
                (participação da família no cumprimento da medida, potencialidades,
                vulnerabilidades, violências, riscos e conflitos)
              </SpanSubtitleCustom>
            </SpanTitleCustom>
          </Grid>
          <Grid item xs={12}>
            <FormInput
              name={'descricaoRelacoesFamiliares'}
              control={control}
              multiline
              minRows={4}
              disabled={doNotHasEditPermission}
            />
          </Grid>
          <Grid item xs={12} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <SpanTitleCustom>
              OUTRAS REFERÊNCIAS FAMILIARES, AFETIVAS E INSTITUCIONAIS
            </SpanTitleCustom>
          </Grid>
          <Grid item xs={12}>
            <FamiliarCompositionList
              watch={watch}
              control={control}
              fieldArrayName={'outrasReferenciasFamiliares'}
              initialValuesFieldName={'outrasReferenciasFamiliares'}
              setValue={setValue}
              initialValues={[familiarReferenceV2Init]}
              disabled={doNotHasEditPermission}
              buttonLabel={'REFERÊNCIAS FAMILIARES'}
              familiarReferenceOptions={familiarReferenceOptions}
              hasAddress
              labels={{
                labelDeleteItem: 'Tem certeza que deseja excluir a referência familiar?',
              }}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
